import type { ReactNode } from 'react';

import { Box, Button, Stack, Typography } from '@mui/material';

import StatusBar from '@/designs-dev/device/StatusBar';
import HomeIndicator from '@/designs-dev/device/HomeIndicator';
import ScreenRoot from '@/components/wrapped/ScreenRoot';
import AccountBar, { type Account } from '@/karehero-app/access/AccountBar';

// Replica of the production CenterPageTemplate + FloatingContent pairing
// (lib/llama/src/pwa) used by the Account Created and Access Verified
// screens. Rebuilt locally so the prototype can diverge without touching
// llama.

export interface CenteredScreenAction {
  label: string;
  variant: 'primary' | 'secondary';
  onClick: () => void;
}

const variantMap = { primary: 'contained', secondary: 'outlined' } as const;

// The heading sits here on every screen instead of wherever centring the whole
// block put it, so it can be scanned down a flow without moving. Everything under
// it still floats, centred in what is left — only the heading is pinned.
const HEADING_TOP = '120px';

// The band the heading block occupies, fixed rather than as tall as its own
// copy. Everything below floats in what is left, so a screen with a longer
// blurb used to float its buttons higher than the screen before it: the primary
// is the thing you press twice in a row without looking, and it has to be in
// one place. Wide enough for a heading, four lines of blurb and a line under
// them; a screen with more than that pushes down, as it should.
const HEADING_BAND = '320px';

// The band the actions sit in, at the bottom and always the same height: room
// for two large buttons and the gap between them — 56 + 16 + 56. Loft puts
// spacing back on MUI's 8px scale (see theme.tsx), so the gap of 2 is 16.
//
// Fixed rather than as tall as the buttons it holds, and the primary anchored
// to the top of it, so the primary lands in one place whether a screen offers
// one action or two. Continue and 'I'm happy with that' are pressed one after
// the other without looking, and centring the block put them a button's height
// apart.
const ACTION_BAND = '128px';

const CenteredScreen = ({
  title,
  description,
  note,
  actionNote,
  actions,
  children,
  account,
}: {
  title: string;
  description?: string;
  /**
   * A line under the description, in the pinned block rather than the floating
   * one. For something that belongs to the blurb, like a link to the document
   * it is summarising.
   */
  note?: ReactNode;
  /**
   * A line directly above the actions, for something the press itself commits
   * to — a consent the button carries rather than a statement about the blurb.
   * It sits outside the fixed action band, so a screen that uses it drops its
   * primary by the height of the line. That is the point: the sentence has to
   * read as attached to the button.
   */
  actionNote?: ReactNode;
  actions: CenteredScreenAction[];
  children?: ReactNode;
  /** The app bar. See StepScreen's own `account`. */
  account?: Account;
}) => (
  <ScreenRoot>
    <StatusBar />
    {account && <AccountBar {...account} />}
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        px: 3,
        py: 2,
        overflow: 'auto',
      }}
    >
      <Stack
        sx={{
          gap: 1,
          pt: HEADING_TOP,
          minHeight: HEADING_BAND,
          flexShrink: 0,
        }}
      >
        <Typography sx={{ textAlign: 'center' }} variant='h1'>
          {title}
        </Typography>
        {description && (
          <Typography sx={{ textAlign: 'center' }} variant='subtitle1'>
            {description}
          </Typography>
        )}
        {note && <Box sx={{ textAlign: 'center' }}>{note}</Box>}
      </Stack>
      {/* Whatever the screen puts between the heading and the actions, in the
          room left over. */}
      <Stack sx={{ flex: 1, minHeight: 0, justifyContent: 'center', gap: 4 }}>
        {children}
      </Stack>

      {actionNote && (
        <Box sx={{ flexShrink: 0, textAlign: 'center', pb: 2 }}>
          {actionNote}
        </Box>
      )}

      <Stack
        sx={{
          gap: 2,
          flexShrink: 0,
          minHeight: ACTION_BAND,
          // Top, not centre: the primary is the first child, so anchoring the
          // stack is what keeps the primary still.
          justifyContent: 'flex-start',
        }}
      >
        {actions.map((action) => (
          <Button
            key={action.label}
            aria-label={action.label}
            variant={variantMap[action.variant]}
            size='large'
            onClick={action.onClick}
          >
            {action.label}
          </Button>
        ))}
      </Stack>
    </Box>
    <HomeIndicator />
  </ScreenRoot>
);

export default CenteredScreen;
